import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star, Trophy, Lightbulb, CheckCircle, ChevronRight, Play, Volume2 } from 'lucide-react';
import { Card } from '../components/Card';
interface RhythmGameModuleProps {
  onBack: () => void;
}
// Audio helper
function playDrum(type: 'kick' | 'snare' | 'hihat' = 'kick') {
  const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  const osc = audioContext.createOscillator();
  const gain = audioContext.createGain();
  switch (type) {
    case 'kick':
      osc.frequency.setValueAtTime(150, audioContext.currentTime);
      osc.frequency.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      break;
    case 'snare':
      osc.frequency.setValueAtTime(200, audioContext.currentTime);
      break;
    case 'hihat':
      osc.frequency.setValueAtTime(800, audioContext.currentTime);
      break;
  }
  osc.connect(gain);
  gain.connect(audioContext.destination);
  gain.gain.setValueAtTime(0.5, audioContext.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
  osc.start(audioContext.currentTime);
  osc.stop(audioContext.currentTime + 0.2);
}
export function RhythmGameModule({
  onBack
}: RhythmGameModuleProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [score, setScore] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const [userPattern, setUserPattern] = useState<boolean[]>([]);
  const [showResult, setShowResult] = useState(false);
  const lessonSteps = [{
    type: 'learn',
    title: '什么是节奏？',
    icon: '🥁',
    content: {
      definition: '节奏是音乐中声音的长短和强弱的组合规律，是音乐的"心跳"。',
      keyPoints: ['节奏让音乐有了"动感"和"律动"', '节拍是节奏的基本单位，像心跳一样规律', '常见的节拍有：2/4拍、3/4拍、4/4拍'],
      example: '想象一下：走路的脚步声"咚-咚-咚-咚"就是一种节奏！',
      visual: '👣 咚 👣 咚 👣 咚 👣 咚'
    }
  }, {
    type: 'interactive',
    title: '感受节拍',
    icon: '💓',
    content: {
      instruction: '点击鼓面，跟着节拍一起打！每个方块代表一拍。',
      pattern: [true, false, true, false, true, false, true, false],
      bpm: 90
    }
  }, {
    type: 'learn2',
    title: '认识音符时值',
    icon: '🎵',
    content: {
      notes: [{
        name: '全音符',
        beats: 4,
        symbol: '𝅝',
        desc: '唱4拍'
      }, {
        name: '二分音符',
        beats: 2,
        symbol: '𝅗𝅥',
        desc: '唱2拍'
      }, {
        name: '四分音符',
        beats: 1,
        symbol: '♩',
        desc: '唱1拍'
      }, {
        name: '八分音符',
        beats: 0.5,
        symbol: '♪',
        desc: '唱半拍'
      }]
    }
  }, {
    type: 'quiz',
    title: '节奏模仿',
    icon: '🎯',
    content: {
      instruction: '听一段节奏，然后用下方的按钮模仿出来！',
      targetPattern: [true, true, false, true, false, true, true, false],
      tolerance: 2 // Allow 2 mistakes
    }
  }, {
    type: 'game',
    title: '节奏大师挑战',
    icon: '🏆',
    content: {
      instruction: '跟着亮起的节拍点击！看看你能得多少分！'
    }
  }, {
    type: 'summary',
    title: '知识总结',
    icon: '📝',
    content: {
      points: [{
        emoji: '🥁',
        text: '节奏是音乐的"心跳"，让音乐有律动感'
      }, {
        emoji: '💓',
        text: '节拍是节奏的基本单位，像心跳一样规律'
      }, {
        emoji: '🎵',
        text: '不同音符有不同时值：全音符4拍、二分音符2拍、四分音符1拍'
      }, {
        emoji: '🎯',
        text: '通过练习可以培养稳定的节奏感'
      }]
    }
  }];
  const currentLesson = lessonSteps[currentStep];
  const playPattern = useCallback((pattern: boolean[], bpm: number = 90) => {
    setIsPlaying(true);
    const interval = 60000 / bpm / 2; // 8th notes
    pattern.forEach((beat, i) => {
      setTimeout(() => {
        setCurrentBeat(i);
        if (beat) playDrum('kick');
      }, i * interval);
    });
    setTimeout(() => {
      setIsPlaying(false);
      setCurrentBeat(-1);
    }, pattern.length * interval);
  }, []);
  const handleNextStep = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }
    setUserPattern([]);
    setShowResult(false);
    if (currentStep < lessonSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  const renderStepContent = () => {
    switch (currentLesson.type) {
      case 'learn':
        const learnContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-637">
            <Card className="p-6 bg-green-50 border-green-100" data-id="element-638">
              <div className="flex items-start space-x-4" data-id="element-639">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" data-id="element-640">
                  💡
                </div>
                <div data-id="element-641">
                  <h4 className="font-bold text-green-800 mb-2" data-id="element-642">定义</h4>
                  <p className="text-green-700" data-id="element-643">{learnContent.definition}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 bg-white" data-id="element-644">
              <h4 className="font-bold text-slate-800 mb-4 flex items-center" data-id="element-645">
                <Lightbulb className="mr-2 text-amber-500" size={20} data-id="element-646" />
                重要知识点
              </h4>
              <div className="space-y-3" data-id="element-647">
                {learnContent.keyPoints.map((point: string, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.2
              }} className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg" data-id="element-648">
                    <CheckCircle className="text-green-500 flex-shrink-0" size={20} data-id="element-649" />
                    <span className="text-slate-700" data-id="element-650">{point}</span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-100" data-id="element-651">
              <h4 className="font-bold text-amber-800 mb-2" data-id="element-652">🌟 生活中的例子</h4>
              <p className="text-amber-700 mb-4" data-id="element-653">{learnContent.example}</p>
              <div className="text-center text-2xl py-4 bg-white rounded-xl font-mono" data-id="element-654">
                {learnContent.visual}
              </div>
            </Card>
          </div>;
      case 'interactive':
        const interactiveContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-655">
            <Card className="p-6 bg-purple-50 border-purple-100" data-id="element-656">
              <p className="text-purple-700 font-medium text-center" data-id="element-657">
                {interactiveContent.instruction}
              </p>
            </Card>

            {/* Beat Visualization */}
            <div className="flex justify-center space-x-2" data-id="element-658">
              {interactiveContent.pattern.map((beat: boolean, i: number) => <motion.div key={i} animate={{
              scale: currentBeat === i ? 1.2 : 1,
              backgroundColor: currentBeat === i ? '#8B5CF6' : beat ? '#E9D5FF' : '#F1F5F9'
            }} className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-slate-600" data-id="element-659">
                  {i + 1}
                </motion.div>)}
            </div>

            {/* Drum Pad */}
            <div className="flex justify-center" data-id="element-660">
              <motion.button whileTap={{
              scale: 0.9
            }} onClick={() => playDrum('kick')} className="w-32 h-32 bg-gradient-to-br from-orange-400 to-red-500 rounded-full shadow-xl flex items-center justify-center text-white text-4xl" data-id="element-661">
                🥁
              </motion.button>
            </div>

            <button onClick={() => playPattern(interactiveContent.pattern, interactiveContent.bpm)} disabled={isPlaying} className="w-full py-4 bg-purple-600 text-white rounded-xl font-bold disabled:opacity-50" data-id="element-662">
              {isPlaying ? '🔊 播放中...' : '▶️ 播放示范节奏'}
            </button>
          </div>;
      case 'learn2':
        const learn2Content = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-663">
            <Card className="p-6 bg-blue-50 border-blue-100" data-id="element-664">
              <h4 className="font-bold text-blue-800 mb-4 text-center" data-id="element-665">
                音符时值对照表
              </h4>
              <div className="space-y-4" data-id="element-666">
                {learn2Content.notes.map((note: any, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: i * 0.15
              }} className="flex items-center justify-between p-4 bg-white rounded-xl" data-id="element-667">
                    <div className="flex items-center space-x-4" data-id="element-668">
                      <span className="text-4xl" data-id="element-669">{note.symbol}</span>
                      <div data-id="element-670">
                        <div className="font-bold text-slate-800" data-id="element-671">
                          {note.name}
                        </div>
                        <div className="text-sm text-slate-500" data-id="element-672">
                          {note.desc}
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-1" data-id="element-673">
                      {Array.from({
                    length: note.beats
                  }).map((_, j) => <div key={j} className="w-6 h-6 bg-blue-500 rounded" data-id="element-674"></div>)}
                      {note.beats < 1 && <div className="w-3 h-6 bg-blue-500 rounded" data-id="element-675"></div>}
                    </div>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-4 bg-amber-50 border-amber-100" data-id="element-676">
              <p className="text-amber-700 text-sm text-center" data-id="element-677">
                💡 记住：音符越"空"，时值越长；音符越"实"，时值越短
              </p>
            </Card>
          </div>;
      case 'quiz':
        const quizContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-678">
            <Card className="p-6 bg-teal-50 border-teal-100" data-id="element-679">
              <p className="text-teal-700 font-medium text-center" data-id="element-680">
                {quizContent.instruction}
              </p>
            </Card>

            {/* Target Pattern Display */}
            <div className="flex justify-center space-x-2 mb-4" data-id="element-681">
              {quizContent.targetPattern.map((beat: boolean, i: number) => <div key={i} className={`w-10 h-10 rounded-lg flex items-center justify-center ${beat ? 'bg-teal-500 text-white' : 'bg-slate-200'}`} data-id="element-682">
                  {beat ? '●' : '○'}
                </div>)}
            </div>

            <button onClick={() => playPattern(quizContent.targetPattern, 90)} disabled={isPlaying} className="w-full py-3 bg-teal-600 text-white rounded-xl font-bold disabled:opacity-50" data-id="element-683">
              {isPlaying ? '🔊 播放中...' : '▶️ 听一遍节奏'}
            </button>

            {/* User Input */}
            <Card className="p-6 bg-white" data-id="element-684">
              <h4 className="font-bold text-slate-800 mb-4 text-center" data-id="element-685">
                你的节奏：
              </h4>
              <div className="flex justify-center space-x-2 mb-4" data-id="element-686">
                {Array.from({
                length: 8
              }).map((_, i) => <button key={i} onClick={() => {
                const newPattern = [...userPattern];
                if (newPattern.length <= i) {
                  while (newPattern.length < i) newPattern.push(false);
                  newPattern.push(true);
                } else {
                  newPattern[i] = !newPattern[i];
                }
                setUserPattern(newPattern);
                if (newPattern[i]) playDrum('kick');
              }} className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-all ${userPattern[i] ? 'bg-orange-500 text-white border-orange-500' : 'bg-white border-slate-200 hover:border-orange-300'}`} data-id="element-687">
                    {userPattern[i] ? '●' : '○'}
                  </button>)}
              </div>

              <div className="flex space-x-3" data-id="element-688">
                <button onClick={() => setUserPattern([])} className="flex-1 py-2 border border-slate-200 rounded-xl font-medium text-slate-600" data-id="element-689">
                  清除
                </button>
                <button onClick={() => {
                // Check answer
                let correct = 0;
                quizContent.targetPattern.forEach((beat: boolean, i: number) => {
                  if (userPattern[i] === beat) correct++;
                });
                if (correct >= 8 - quizContent.tolerance) {
                  setScore(s => s + 20);
                }
                setShowResult(true);
              }} className="flex-1 py-2 bg-orange-500 text-white rounded-xl font-bold" data-id="element-690">
                  提交答案
                </button>
              </div>
            </Card>

            {showResult && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} data-id="element-691">
                <Card className="p-6 bg-green-50 border-green-200" data-id="element-692">
                  <h4 className="font-bold text-green-800 mb-2" data-id="element-693">
                    {userPattern.filter((b, i) => b === quizContent.targetPattern[i]).length >= 6 ? '🎉 太棒了！' : '💪 继续努力！'}
                  </h4>
                  <p className="text-green-700" data-id="element-694">
                    你答对了{' '}
                    {userPattern.filter((b, i) => b === quizContent.targetPattern[i]).length}{' '}
                    / 8 个节拍！
                  </p>
                </Card>
              </motion.div>}
          </div>;
      case 'game':
        return <div className="space-y-6" data-id="element-695">
            <Card className="p-6 bg-gradient-to-br from-purple-500 to-pink-500 text-white text-center" data-id="element-696">
              <Trophy className="mx-auto mb-4" size={48} data-id="element-697" />
              <h3 className="text-2xl font-bold mb-2" data-id="element-698">节奏大师挑战</h3>
              <p className="opacity-90" data-id="element-699">跟着节拍点击，获得高分！</p>
            </Card>

            {/* Simple Rhythm Game */}
            <div className="grid grid-cols-4 gap-4" data-id="element-700">
              {[0, 1, 2, 3].map(i => <motion.button key={i} whileTap={{
              scale: 0.9
            }} onClick={() => {
              playDrum(['kick', 'snare', 'hihat', 'kick'][i] as any);
              setScore(s => s + 5);
            }} className={`aspect-square rounded-2xl text-4xl shadow-lg ${['bg-red-500', 'bg-yellow-500', 'bg-green-500', 'bg-blue-500'][i]}`} data-id="element-701">
                  {['🥁', '👏', '🔔', '💥'][i]}
                </motion.button>)}
            </div>

            <Card className="p-4 bg-slate-50" data-id="element-702">
              <p className="text-slate-600 text-sm text-center" data-id="element-703">
                💡 自由敲击，感受节奏的乐趣！每次点击得5分
              </p>
            </Card>
          </div>;
      case 'summary':
        const summaryContent = currentLesson.content as any;
        return <div className="space-y-6" data-id="element-704">
            <Card className="p-6 bg-gradient-to-br from-green-50 to-teal-50" data-id="element-705">
              <div className="text-center mb-6" data-id="element-706">
                <div className="text-6xl mb-4" data-id="element-707">🎓</div>
                <h3 className="text-2xl font-bold text-slate-800" data-id="element-708">
                  恭喜完成学习！
                </h3>
                <p className="text-slate-500" data-id="element-709">你已经掌握了节奏的基础知识</p>
              </div>

              <div className="space-y-4" data-id="element-710">
                {summaryContent.points.map((point: any, i: number) => <motion.div key={i} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.15
              }} className="flex items-center space-x-4 p-4 bg-white rounded-xl shadow-sm" data-id="element-711">
                    <span className="text-2xl" data-id="element-712">{point.emoji}</span>
                    <span className="text-slate-700 font-medium" data-id="element-713">
                      {point.text}
                    </span>
                  </motion.div>)}
              </div>
            </Card>

            <Card className="p-6 bg-amber-50 border-amber-200" data-id="element-714">
              <div className="flex items-center justify-between" data-id="element-715">
                <div data-id="element-716">
                  <h4 className="font-bold text-amber-800" data-id="element-717">本次学习得分</h4>
                  <p className="text-amber-600 text-sm" data-id="element-718">
                    继续努力，争取更高分！
                  </p>
                </div>
                <div className="flex items-center space-x-2" data-id="element-719">
                  <Star className="text-amber-500" fill="currentColor" data-id="element-720" />
                  <span className="text-3xl font-bold text-amber-700" data-id="element-721">
                    {score}
                  </span>
                </div>
              </div>
            </Card>
          </div>;
      default:
        return null;
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen w-full flex flex-col bg-slate-50" data-id="element-722">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-50" data-id="element-723">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-724">
          <button onClick={onBack} className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 bg-slate-50 px-4 py-2 rounded-full" data-id="element-725">
            <ArrowLeft size={20} data-id="element-726" />
            <span className="font-bold text-sm" data-id="element-727">返回</span>
          </button>

          <h1 className="text-xl font-bold text-slate-900" data-id="element-728">节奏大师</h1>

          <div className="flex items-center space-x-2 bg-amber-50 px-4 py-2 rounded-full" data-id="element-729">
            <Star className="text-amber-500" size={18} fill="currentColor" data-id="element-730" />
            <span className="font-bold text-amber-700" data-id="element-731">{score}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="max-w-4xl mx-auto px-4 pb-4" data-id="element-732">
          <div className="flex items-center space-x-2" data-id="element-733">
            {lessonSteps.map((step, i) => <div key={i} className="flex-1" data-id="element-734">
                <div className={`h-2 rounded-full ${completedSteps.includes(i) ? 'bg-green-500' : i === currentStep ? 'bg-green-400' : 'bg-slate-200'}`} data-id="element-735" />
              </div>)}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8" data-id="element-736">
        <div className="max-w-2xl mx-auto" data-id="element-737">
          {/* Step Header */}
          <div className="mb-6 text-center" data-id="element-738">
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-4" data-id="element-739">
              <span className="text-2xl" data-id="element-740">{currentLesson.icon}</span>
              <span className="text-sm font-medium text-slate-500" data-id="element-741">
                第 {currentStep + 1} 步 / 共 {lessonSteps.length} 步
              </span>
            </div>
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-742">
              {currentLesson.title}
            </h2>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait" data-id="element-743">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.3
          }} data-id="element-744">
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-slate-100 p-4" data-id="element-745">
        <div className="max-w-2xl mx-auto flex justify-between items-center" data-id="element-746">
          <button onClick={() => {
          setUserPattern([]);
          setShowResult(false);
          if (currentStep > 0) setCurrentStep(currentStep - 1);
        }} disabled={currentStep === 0} className="px-6 py-2 rounded-full border border-slate-200 text-slate-600 font-medium disabled:opacity-50" data-id="element-747">
            上一步
          </button>

          <button onClick={currentStep === lessonSteps.length - 1 ? onBack : handleNextStep} className="px-8 py-2 rounded-full bg-green-600 text-white font-bold shadow-md hover:bg-green-700" data-id="element-748">
            {currentStep === lessonSteps.length - 1 ? '完成学习' : '下一步'}
            {currentStep < lessonSteps.length - 1 && <ChevronRight className="inline ml-1" size={18} data-id="element-749" />}
          </button>
        </div>
      </div>
    </motion.div>;
}